import { createMuiTheme } from "@material-ui/core/styles";
import { Overrides as CoreOverrides } from '@material-ui/core/styles/overrides';
import { AutocompleteClassKey } from '@material-ui/lab';
import { CSSProperties } from '@material-ui/styles';

const palette = {
  primary: {
    main: "#000000", // black
    light: "#0034B6", // dark blue
    dark: "#000000",
  },
  secondary: {
    main: "#FFFFFF", // white
    light: "#F7F7F7",
  },
  text: {
    primary: "#000", // black
    secondary: "#0034B6",
    light: "#F7F7F7",
  },
  info: { main: "#0034B6" }, // darkblue
};

interface Overrides extends CoreOverrides {
  MuiAutocomplete?: Partial<Record<AutocompleteClassKey, CSSProperties | (() => CSSProperties)>> | undefined;
}

export const themeClasses = {
  CommonNavbar: {
    root: {
      zIndex: 2000,
      backgroundColor: "white !important",
      boxShadow: "0px 21.7852px 43.5704px rgba(0, 0, 0, 0.05) !important",
    },
  },
  MuiAutocomplete: {
    inputRoot: { padding: "0px !important" },
    root: {
      paddingLeft: 0,
      width: "100%",
    },
    option: {
      // Hover
      '&[data-focus="true"]': {
        backgroundColor: "#fff",
        borderColor: "transparent",
        color: "#0059E2",
      },
      // Selected
      '&[aria-selected="true"]': {
        backgroundColor: "#fff",
        borderColor: "transparent",
        color: "#4a4a4a",
      },
      "&:hover": {
        backgroundColor: "white",
        borderColor: "transparent",
        color: "#0059E2",
      },
    },
    listbox: {
      border: "1px solid rgb(217, 217, 217)",
      borderRadius: 4,
      backgroundColor: "#F7F7F7",
    },
    noOptions: {
      border: "1px solid rgb(217, 217, 217)",
      borderRadius: 4,
      backgroundColor: "#F7F7F7",
    },
    popupIndicator: {
      position: "absolute",
      right: "0px",
      zIndex: 100,
      backgroundColor: "transparent !important",
      boxShadow: "unset !important",
      color: "rgba(0, 0, 0, 0.54) !important",
      "&:hover": { backgroundColor: "transparent !important" },
      "&:disabled": { backgroundColor: "transparent !important" },
      "&:active": { backgroundColor: "transparent !important" },
    },
    clearIndicator: { padding: "0px" },
    endAdornment: { width: "20px" },
  },
  MuiButton: {
    label: {
      textTransform: "capitalize",
      fontFamily: "NimbusSans-Regular",
      fontSize: "16px",
      lineHeight: "24px",
      padding: 4,
      fontWeight: 600,
    },
    root: {
      borderRadius: 0,
      width: "100%",
    },
    contained: {
      boxShadow: "unset",
      "&:hover": { boxShadow: "unset" },
      "&:disabled": {
        background: "#CCD1DB",
        backgroundColor: "#CCD1DB",
        color: "#fff",
        borderRadius: "4px",
        // width: "50%",
      },
    },
  },
  MuiCard: { root: { background: "#FFFFFF" } },
  MuiCheckbox: {
    root: {
      color: "#000000",
      paddingLeft: 0,
    },
    colorPrimary: {
      color: "#000000",
      paddingLeft: 0,
      "&$checked": {
        color: "#000000",
        paddingLeft: 0,
      },
    },
  },
  MuiFormHelperText: {
    root: {
      display: "none",
      "& .Mui-error": { display: "none" },
    },
  },
  MuiInput: {
    underline: {
      "&:before": {
        borderBottom: "none",
        transition: "none",
      },
      "&:after": {
        borderBottom: "none",
        transition: "none",
      },
      "&:hover": {
        borderBottom: "none",
        transition: "none",
      },
      "&:focus": {
        borderColor: "#0034B6",
        // border: '1px solid black',
      },
    },
  },
  MuiInputBase: {
    input: {
      fontSize: 16,
      "&:focus": {
        borderColor: "#0034B6",
        // border: '1px solid black',
      },
      "&:hover": {
        borderBottom: "none",
        transition: "none",
      },
    },
  },
  MuiInputLabel: {
    outlined: {
      color: "#7a7a7a",
      transform: "translate(14px, 12px) scale(1)",
    },
  },
  MuiMenu: {
    paper: {
      width: "200px",
      boxShadow: "0px 4px 22px rgb(0 0 0 / 15%)",
      borderRadius: "10px",
      overflow: "visible",
    },
  },
  MuiMenuItem: {
    root: {
      "&:hover": {
        backgroundColor: "#000",
        color: "#fff",
      },
    },
  },
  MuiOutlinedInput: {
    root: {
      borderRadius: 0,
      fontFamily: "NimbusSans-Regular",
      fontSize: 14,
      width: "100%",
      height: "40px",
      padding: "0px !important",
      "&:focus": {
        borderColor: "#0034B6",
        // border: '1px solid black',
      },
      "&:placeholder": {
        fontFamily: "NimbusSans-Regular",
        fontSize: 16,
        color: "black",
      },
      "&:label": { color: "black" },
      "&$focused $notchedOutline": {
        borderColor: "#0034B6",
        borderWidth: 1,
        color: "#0034B6",
      },
    },
    inputAdornedStart: { border: "none" },
    input: {
      padding: 0,
      width: "100%",
      // border: "1px solid black",
      borderRadius: 2,
      height: "40px",
      background: "#fff",
      fontSize: 16,
      fontWeight: 400,
      paddingLeft: 4,
      boxSizing: "border-box",
      outline: "none",
      "&:focus": {
        borderColor: "#0034B6",
        // border: '1px solid black',
      },
      "&:placeholder": {
        fontFamily: "NimbusSans-Regular",
        fontSize: 16,
        color: "black",
      },
    },
    notchedOutline: {
      border: "0.5px solid",
      // borderColor: '#0034B6',
      borderColor: "black",
      borderRadius: 2,
      "&:focus": {
        border: "0.5px solid",
        // borderColor: 'black',
        borderColor: "#0034B6",
        borderRadius: 2,
      },
      "&:placeholder": {
        fontFamily: "NimbusSans-Regular",
        fontSize: 16,
        color: "black",
      },
      "& .PrivateNotchedOutline-legendLabelled-25 > span": {
        paddingLeft: 18,
        // visibility: "visible",
      },
      "& .PrivateNotchedOutline-legendLabelled-22 > span": { paddingLeft: 18 },
    },
  },
  MuiPaper: {
    elevation1: {
      boxShadow: "none",
      fontFamily: "NimbusSans-Regular",
      background: "inherit",
      width: "100%",
    },
  },
  MuiSelect: {
    root: {
      textAlign: "initial",
      fontSize: 14,
      "& placeholder": { color: "red" },
      "& .MuiSelect-select": { color: "#000" },
    },
    selectMenu: {
      fontFamily: "NimbusSans-Regular",
      fontSize: 16,
      color: "#7a7a7a",
      background: "#FFF",
    },
    select: {
      color: "#000",
      "& .MuiSelect-select": { color: "#000" },
    },
    outlined: { color: "#000" },
  },
  MuiSlider: {
    rail: {
      backgroundColor: "#DFDFDF",
      height: "6px",
      borderRadius: 4,
    },
    root: { marginTop: "24px" },
    track: {
      backgroundColor: "#0059e2",
      height: "6px",
      borderRadius: "4px",
    },
    thumb: {
      marginTop: "-3px",
      backgroundColor: "#0059e2",
    }
  },
  MuiTableCell: { root: { borderBottom: "0.5px solid #CECECE" } },
  MuiTablePagination: { toolbar: { backgroundColor: "#fff" } },
  MuiTextField: {
    root: {
      backgroundColor: "#FFFFFF",
      color: "black",
      borderRadius: 0,
      width: "100%",
      "&:focus": {
        border: "0.5px solid",
        // borderColor: 'black',
        borderColor: "#0034B6",
        borderRadius: 2,
      },
      "&:placeholder": { color: "black" },
    },
  },
  MuiToolbar: {
    regular: {
      paddingLeft: "11%",
      paddingRight: "11%",
      paddingTop: "0.5%",
      "@media (min-width: 600px)": {
        paddingLeft: "11%",
        paddingRight: "11%",
        paddingTop: "0.5%",
      },
    },
  },
}

export const theme = createMuiTheme({
  palette,
  typography: {
    fontFamily: ["NimbusSans-Regular"].join(","),
    h1: {
      fontSize: "42px",
      lineHeight: "42px",
    },
    h2: {
      fontSize: "27px",
      fontFamily: "NimbusSans-Bold",
      fontWeight: 700,
      lineHeight: "133.84%",
      textDecoration: 'underline'
    },
    h3: {
      fontSize: "20px",
      lineHeight: "143%",
    },
    body1: {
      fontSize: "16px",
      lineHeight: "143%",
    },
    body2: {
      fontWeight: "normal",
      fontSize: "16px",
      lineHeight: "143%",
    },
    subtitle1: {
      fontSize: "16px",
      textTransform: 'capitalize'
    },
    h4: {
      fontSize: "28px",
      lineHeight: "28px",
      fontWeight: 600,
      marginBottom: 6,
      color: "#000000",
    },
    subtitle2: {
      textAlign: "initial",
      fontSize: 18,
      fontWeight: 700,
    },
  },
  overrides: themeClasses as Overrides,
});
